import React from "react";

/**
 * Drop-in replacement for `React.lazy` that recovers from
 * `ChunkLoadError` by reloading the page once.
 *
 * Webpack code-split chunks are content-hashed. After a dev-server
 * HMR cycle (chunk hash reissued) or a production deploy (old chunk
 * URL no longer served) a long-lived tab can hold lazy refs to
 * chunks that 404, and the resulting `ChunkLoadError` reaches React
 * Router's error boundary — which never bubbles back out to
 * `window.error` or `unhandledrejection`. Catching the rejected
 * import promise here, inside the lazy factory, intercepts the error
 * before React.lazy throws it during render and before any error
 * boundary sees it.
 *
 * Strategy: on the first chunk-load failure in this tab, set a
 * sessionStorage sentinel and call `window.location.reload()`. The
 * factory returns a never-resolving promise so React suspends until
 * the page tears down. If the same chunk fails again after the
 * reload (sentinel already set), rethrow so React's normal error
 * boundary can surface it — the chunk is genuinely unavailable and
 * looping wouldn't help.
 *
 * The sentinel is cleared on a successful `window.load`, which is
 * handled in services/monitor-ui/src/index.js.
 */

const RELOAD_FLAG = "__chunkReloadAttempted";

const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === "ChunkLoadError") return true;
  if (typeof err.message === "string" && err.message.includes("Loading chunk")) return true;
  return false;
};

export const lazyWithReload = (factory) =>
  React.lazy(() =>
    factory()
      .then((mod) => {
        // Successful chunk load. Drop any stale reload sentinel left
        // over from an earlier session so the NEXT chunk failure can
        // trigger its own reload — without this, a `1` left in
        // sessionStorage from a prior reload would cause the next
        // failure to skip recovery and surface the error.
        try {
          sessionStorage.removeItem(RELOAD_FLAG);
        } catch {
          // Storage unavailable — harmless, leave the flag as-is.
        }
        return mod;
      })
      .catch((err) => {
        if (isChunkLoadError(err) && typeof window !== "undefined") {
          try {
            if (sessionStorage.getItem(RELOAD_FLAG) !== "1") {
              sessionStorage.setItem(RELOAD_FLAG, "1");
              window.location.reload();
              // Suspend forever; the reload will tear this tree down.
              return new Promise(() => {});
            }
          } catch {
            // sessionStorage may be unavailable (private mode, quota,
            // etc.); fall through to rethrow rather than try to recover.
          }
        }
        throw err;
      }),
  );
